import React, { useEffect, useMemo, useState } from 'react';
import './SupportAttachmentViewerModal.css';

type ViewerAttachment = {
  id: string;
  messageId: string | null;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number;
  createdAt: string;
  downloadPath: string;
  previewPath: string;
};

type Props = {
  attachment: ViewerAttachment | null;
  loadFile: (attachment: ViewerAttachment) => Promise<{ blob: Blob; contentType: string }>;
  loadTextPreview: (attachment: ViewerAttachment) => Promise<{ kind: 'TEXT' | 'DOCUMENT_TEXT' | 'BINARY'; content: string | null }>;
  downloadFile: (attachment: ViewerAttachment) => Promise<void>;
  onClose: () => void;
};

const formatSize = (value: number) => value >= 1048576 ? `${(value / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(value / 1024))} KB`;
const extensionOf = (fileName: string) => fileName.toLowerCase().split('.').pop() || '';

const SupportAttachmentViewerModal: React.FC<Props> = ({ attachment, loadFile, loadTextPreview, downloadFile, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState('');
  const [textContent, setTextContent] = useState<string | null>(null);
  const [documentText, setDocumentText] = useState(false);
  const extension = useMemo(() => extensionOf(attachment?.fileName || ''), [attachment?.fileName]);

  useEffect(() => {
    if (!attachment) return undefined;
    let cancelled = false;
    let createdUrl: string | null = null;
    setLoading(true); setError(null); setObjectUrl(null); setTextContent(null); setDocumentText(false);
    const mime = String(attachment.mimeType || '').toLowerCase();
    const wantsText = mime.startsWith('text/') || ['txt', 'md', 'csv', 'tsv', 'json', 'xml', 'log', 'docx', 'xlsx', 'pptx'].includes(extension);
    const run = async () => {
      if (wantsText) {
        const preview = await loadTextPreview(attachment);
        if (preview.kind !== 'BINARY' && preview.content !== null) {
          if (!cancelled) { setTextContent(preview.content); setDocumentText(preview.kind === 'DOCUMENT_TEXT'); }
          return;
        }
      }
      const file = await loadFile(attachment);
      if (cancelled) return;
      createdUrl = URL.createObjectURL(file.blob);
      setObjectUrl(createdUrl);
      setContentType(file.contentType || mime);
    };
    void run().catch(caught => { if (!cancelled) setError(caught instanceof Error ? caught.message : 'Attachment preview could not be loaded'); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [attachment, extension, loadFile, loadTextPreview]);

  if (!attachment) return null;
  const mime = contentType.toLowerCase();
  const kind = mime.startsWith('image/') ? 'image' : mime === 'application/pdf' || extension === 'pdf' ? 'pdf' : mime.startsWith('video/') ? 'video' : mime.startsWith('audio/') ? 'audio' : 'embedded';
  const download = async () => { setDownloading(true); setError(null); try { await downloadFile(attachment); } catch (caught) { setError(caught instanceof Error ? caught.message : 'Attachment could not be downloaded'); } finally { setDownloading(false); } };

  return <div className="support-attachment-viewer-backdrop">
    <section className="support-attachment-viewer" role="dialog" aria-modal="true" aria-labelledby="support-attachment-viewer-title">
      <header><div><div className="support-attachment-viewer-eyebrow">TICKET ATTACHMENT</div><h2 id="support-attachment-viewer-title">{attachment.fileName}</h2><p>{attachment.mimeType || 'File'} · {formatSize(attachment.sizeBytes)}</p></div><div className="support-attachment-viewer-actions"><button type="button" onClick={() => void download()} disabled={downloading}>{downloading ? 'Preparing...' : 'Download'}</button><button type="button" className="primary" onClick={onClose}>Close</button></div></header>
      <div className="support-attachment-viewer-body">
        {loading && <div className="support-attachment-viewer-state">Loading preview...</div>}
        {error && <div className="support-attachment-viewer-state error">{error}</div>}
        {!loading && !error && textContent !== null && <div className="support-attachment-text-preview">{documentText && <div className="support-attachment-text-note">Document text preview</div>}<pre>{textContent || 'This document contains no readable text.'}</pre></div>}
        {!loading && !error && objectUrl && kind === 'image' && <div className="support-attachment-image-wrap"><img src={objectUrl} alt={attachment.fileName} /></div>}
        {!loading && !error && objectUrl && kind === 'pdf' && <iframe title={attachment.fileName} src={objectUrl} />}
        {!loading && !error && objectUrl && kind === 'video' && <video src={objectUrl} controls />}
        {!loading && !error && objectUrl && kind === 'audio' && <div className="support-attachment-viewer-state"><audio src={objectUrl} controls /></div>}
        {!loading && !error && objectUrl && kind === 'embedded' && <iframe title={attachment.fileName} src={objectUrl} />}
      </div>
    </section>
  </div>;
};

export default SupportAttachmentViewerModal;
